import { GoogleGenerativeAI } from "@google/generative-ai";
import axios from "axios";
import CryptoJS from "crypto-js";
import {
	addDoc,
	collection,
	doc,
	getDoc,
	getDocs,
	increment,
	limit,
	orderBy,
	query,
	setDoc,
	updateDoc,
} from "firebase/firestore";
import {
	AlertCircle,
	Bookmark,
	Loader,
	Mic,
	MicOff,
	Pause,
	Send,
	Volume2,
	VolumeX,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Joyride from "react-joyride";
import { useLocation } from "react-router-dom";
import { INSTRUCTIONS } from "../constants/therapyInstructions";
import { useError } from "../contexts/ErrorContext";
import { useUser } from "../contexts/UserContext";
import { db } from "../firebaseConfig";
import Tooltip from "./Tooltip";
import Triager from "./Triager";
import { VoiceVisualization } from "./VoiceViz";

const Therapist = ({ isFreeAccess = false }) => {
	const { user, encryptionKey } = useUser();
	const [isListening, setIsListening] = useState(false);
	const [isSpeaking, setIsSpeaking] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isBuffering, setIsBuffering] = useState(false);
	const [showSuicidalBanner, setShowSuicidalBanner] = useState(false);
	const [userMessage, setUserMessage] = useState("");
	const [aiResponse, setAiResponse] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [editedMessage, setEditedMessage] = useState("");
	const [messageReported, setMessageReported] = useState(false);
	const [messageEndorsed, setMessageEndorsed] = useState(false);
	const [triageMessage, setTriageMessage] = useState(false);
	const [inputMode, setInputMode] = useState("voice");
	const [textInput, setTextInput] = useState("");
	const [modelReady, setModelReady] = useState(false);
	const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(false);
	const [showTriager, setShowTriager] = useState(false);
	const [runTour, setRunTour] = useState(false);
	const [messageBookmarked, setMessageBookmarked] = useState(false);

	// Refs
	const recognition = useRef(null);
	const voiceEnabledRef = useRef(false);
	const currentMessage = useRef("");
	const recognitionTimeout = useRef(null);
	const speakingComplete = useRef(false);
	const currentSession = useRef(null);
	const currentResponseNum = useRef([0, 0]); //[0] is user and [1] is AI
	const inputModeRef = useRef("voice");
	const chatRef = useRef(null);
	const modelReadyRef = useRef(false);
	const audioElement = useRef(new Audio());
	const sessionPaused = useRef(false);

	const Messenger = { AI: "AI", USER: "USER" };

	const { showError, error, displayError } = useError();

	const location = useLocation();

	const TherapySteps = [
		{
			target: ".therapy-section",
			content:
				"Welcome to your safe space to talk to your personalized therapist, who tailors to your personality, needs, and only gets better with time!",
			placement: "center",
		},
		{
			target: ".voice-output-toggle",
			content:
				"Toggle voice output on/off. When on, Akua will speak responses aloud with your chosen voice.",
			placement: "top",
		},
		{
			target: ".input-mode-toggle",
			content: "Toggle between voice and text input",
			placement: "top",
		},
		{
			target: ".mic-toggle",
			content: "Start talking to Akua and start your reflection journey!",
			placement: "top",
		},
	];

	const prepareModel = useCallback(
		async (userId, displayName, therapyType) => {
			try {
				const sessionsRef = collection(db, "Users", userId, "sessions");
				const q = query(
					sessionsRef,
					orderBy("createdAt", "desc"),
					limit(50)
				);
				const querySnapshot = await getDocs(q);

				let conversationHistory = "";
				querySnapshot.forEach((doc) => {
					const data = doc.data();
					// Add session separator with timestamp
					const sessionDate =
						data.createdAt?.toDate()?.toLocaleString() ||
						"Unknown Date";
					conversationHistory += `\n=== Session from ${sessionDate} ===\n`;

					// Filter and sort message keys to maintain conversation order
					const messageKeys = Object.keys(data)
						.filter((key) => key.startsWith("message_"))
						.sort();

					messageKeys.forEach((key) => {
						const speaker = key.includes("A_User")
							? "User"
							: "Akua";
						// Decrypt the message
						const decryptedMessage = CryptoJS.AES.decrypt(
							data[key],
							encryptionKey
						).toString(CryptoJS.enc.Utf8);
						conversationHistory += `${speaker}: ${decryptedMessage}\n`;
					});
				});

				// Customize system instruction based on therapy type
				let therapyInstruction = "";
				switch (therapyType) {
					case "CBT":
						therapyInstruction = INSTRUCTIONS.CBT;
						break;
					case "DBT":
						therapyInstruction = INSTRUCTIONS.DBT;
						break;
					case "Addiction":
						therapyInstruction = INSTRUCTIONS.Addiction;
						break;
					default:
						therapyInstruction = INSTRUCTIONS.default;
				}

				const systemInstruction = `${therapyInstruction}
			
					The user ${displayName}'s past therapy conversation history is: ${conversationHistory}
					Tailor your responses with respect to and refer to the user's past conversation history where appropriate. At the beginning, feel free to check in on recent issues the user has been facing. 
					Remember to respond in a concise manner, prompt with questions more (but only one or two questions at a time so that users are not overwhelmed), and use natural language without any formatting (No markdown should be used)
					`;

				const genAI = new GoogleGenerativeAI(
					process.env.REACT_APP_GEMINI_API_KEY
				);
				const model = genAI.getGenerativeModel({
					model: "gemini-1.5-flash",
					systemInstruction,
				});
				chatRef.current = model.startChat();
				setModelReady(true);
				modelReadyRef.current = true;
			} catch (error) {
				console.error("Error preparing model:", error);
				displayError("Failed to prepare conversation model");
				modelReadyRef.current = false;
			}
		},
		[displayError]
	);

	useEffect(() => {
		const initializeComponent = async () => {
			try {
				const userRef = doc(db, "Users", user.uid);
				const userSnap = await getDoc(userRef);
				const userData = userSnap.data();

				if (!userData.therapyDemo) {
					setRunTour(true);
					await updateDoc(userRef, { therapyDemo: true });
				}

				const userDisplayName =
					user.displayName?.split(" ")[0] || "there";
				setAiResponse(`Hi ${userDisplayName}, how are you doing?`);

				if (!userData?.triage) {
					setShowTriager(true);
				} else {
					await prepareModel(
						user.uid,
						user.displayName,
						userData.triage
					);
					// Check if there's an initial message from topic selection
					if (
						location.state?.initialMessage &&
						modelReadyRef.current
					) {
						setUserMessage(location.state.initialMessage);
						currentMessage.current = location.state.initialMessage;
						currentResponseNum.current[0] += 1;
						saveEveryMessage(
							location.state.initialMessage,
							Messenger.USER
						);
						setIsBuffering(true);
						fetchGeminiResponse();
					}
				}
			} catch (error) {
				console.error("Error initializing component:", error);
			} finally {
				setIsLoading(false);
			}
		};

		initializeComponent();
	}, [user, prepareModel]);

	useEffect(() => {
		recognition.current = new (window.SpeechRecognition ||
			window.webkitSpeechRecognition)();
		recognition.current.continuous = true;
		recognition.current.interimResults = false;
		recognition.current.lang = "en-US";

		recognition.current.onstart = () => setIsListening(true);
		recognition.current.onend = () => {
			if (
				!speakingComplete.current &&
				!isEditing &&
				inputModeRef.current === "voice" &&
				modelReadyRef.current &&
				!sessionPaused.current
			) {
				console.log("restarting recognition");
				try {
					recognition.current.start();
				} catch (error) {
					console.error("Error restarting recognition:", error);
				}
			} else if (
				!isEditing &&
				inputModeRef.current === "voice" &&
				modelReadyRef.current &&
				!sessionPaused.current
			) {
				setIsListening(false);
				setIsBuffering(true);
				currentResponseNum.current[0] += 1;
				saveEveryMessage(currentMessage.current, Messenger.USER);
				fetchGeminiResponse();
			} else {
				setIsListening(false);
			}
		};

		recognition.current.onresult = (event) => {
			if (recognitionTimeout.current)
				clearTimeout(recognitionTimeout.current);
			if (isEditing) return;
			const lastResultIndex = event.results.length - 1;
			const transcriptResult =
				event.results[lastResultIndex][0].transcript;
			currentMessage.current += " " + transcriptResult;
			setUserMessage((prev) => prev + " " + transcriptResult);

			recognitionTimeout.current = setTimeout(() => {
				speakingComplete.current = true;
				recognition.current.stop();
			}, 6000);
		};

		recognition.current.onerror = (event) => {
			console.error("Speech recognition error", event.error);
			setIsListening(false);
		};

		return () => {
			recognition.current.abort();
			if (recognitionTimeout.current)
				clearTimeout(recognitionTimeout.current);
		};
		// eslint-disable-next-line
	}, [isEditing]);

	const fetchGeminiResponse = async () => {
		if (!modelReadyRef.current) {
			displayError("Model not ready yet. Please try again.");
			setIsBuffering(false);
			return;
		}

		try {
			let result = await chatRef.current.sendMessage(
				currentMessage.current
			);
			const aiResponseContent = result.response.text();
			setMessageBookmarked(false);
			setMessageReported(false);
			setMessageEndorsed(false);
			currentResponseNum.current[1] += 1;
			saveEveryMessage(aiResponseContent, Messenger.AI);
			if (aiResponseContent.toLowerCase().includes("988")) {
				setShowSuicidalBanner(true);
			}
			generateAudio(aiResponseContent);
		} catch (error) {
			console.error("Error fetching OpenAI response:", error);
			displayError(error.message);
		}
	};

	const generateAudio = async (aiResponseContent) => {
		try {
			if (!voiceEnabledRef.current) {
				setIsLoading(false);
				setAiResponse(aiResponseContent);
				return;
			}
			const response = await axios.post(
				"https://api.elevenlabs.io/v1/text-to-speech/CAm8Nf1Krs1nSXtbaMmI",
				{ text: aiResponseContent },
				{
					headers: {
						"Content-Type": "application/json",
						"xi-api-key": process.env.REACT_APP_ELEVEN_API,
					},
					responseType: "arraybuffer",
				}
			);
			const audioBlob = new Blob([response.data], {
				type: "audio/mpeg",
			});
			const url = URL.createObjectURL(audioBlob);
			setAiResponse(aiResponseContent);
			playAudio(url);
		} catch (error) {
			console.error("Error during text-to-speech:", error);
			displayError(error.message);
			setAiResponse(aiResponseContent);
		} finally {
			setIsBuffering(false);
		}
	};

	const playAudio = (url) => {
		audioElement.current.src = url;
		audioElement.current.play();
		setIsSpeaking(true);
		audioElement.current.onended = () => {
			setIsSpeaking(false);
			if (inputModeRef.current === "voice") {
				// Check input mode
				startListening();
			}
		};
	};

	const startListening = () => {
		if (!modelReadyRef.current) {
			displayError(
				"Model not ready yet. Please wait a moment and try again."
			);
			return;
		}

		sessionPaused.current = false;
		speakingComplete.current = false;
		setUserMessage("");
		currentMessage.current = "";
		try {
			recognition.current.start();
		} catch (error) {
			console.error("Error starting recognition:", error);
		}
	};

	const stopListening = () => {
		speakingComplete.current = true;
		setIsListening(false);
		recognition.current.stop();
		if (recognitionTimeout.current)
			clearTimeout(recognitionTimeout.current);
	};

	const stopSpeaking = (forceListenAfter = false) => {
		if (isSpeaking || forceListenAfter) {
			if (audioElement.current) {
				audioElement.current.pause();
				audioElement.current.currentTime = 0;
			}
			setIsSpeaking(false);
			if (inputModeRef.current === "voice" && !sessionPaused.current) {
				startListening();
			}
		}
	};

	const handleMicToggle = () => {
		sessionPaused.current = false;
		if (isListening) {
			stopListening();
		} else {
			stopSpeaking(true);
		}
	};

	const handleEditToggle = () => {
		setIsEditing(!isEditing);
		setEditedMessage(userMessage);
	};

	const handleEditChange = (e) => {
		setEditedMessage(e.target.value);
	};

	const handleEditSubmit = () => {
		currentMessage.current = editedMessage;
		setUserMessage(editedMessage);
		saveEveryMessage(editedMessage, Messenger.USER);
		setIsEditing(false);
		setIsListening(false);
		setIsBuffering(true);
		fetchGeminiResponse();
	};

	const reportMessage = async () => {
		if (user && aiResponse) {
			try {
				const reportRef = doc(
					db,
					"reportedMessages",
					new Date().getTime().toString()
				);
				await setDoc(reportRef, {
					message: aiResponse,
					user: user.uid,
					time: new Date(),
				});
				setMessageReported(true);
			} catch (error) {
				console.error("Error reporting message:", error);
				displayError("Failed to report message. Please try again.");
			}
		}
	};

	const endorseMessage = async () => {
		if (user && aiResponse) {
			try {
				const endorseRef = doc(
					db,
					"endorsedMessages",
					new Date().getTime().toString()
				);
				await setDoc(endorseRef, {
					message: aiResponse,
					user: user.uid,
					time: new Date(),
				});
				setMessageEndorsed(true);
			} catch (error) {
				console.error("Error endorsing message:", error);
				displayError("Failed to endorse message. Please try again.");
			}
		}
	};

	const bookmarkMessage = async () => {
		if (user && aiResponse && !messageBookmarked) {
			try {
				const bookmarksRef = collection(
					db,
					"Users",
					user.uid,
					"bookmarks"
				);
				setMessageBookmarked(true);

				const encryptedMessage = CryptoJS.AES.encrypt(
					aiResponse,
					encryptionKey
				).toString();

				await addDoc(bookmarksRef, {
					suggestion: encryptedMessage,
					createdAt: new Date(),
					type: "therapy",
				});
			} catch (error) {
				setMessageBookmarked(false);
				console.error("Error bookmarking message:", error);
				displayError("Failed to bookmark message");
			}
		}
	};

	const saveEveryMessage = async (message, messenger) => {
		if (!currentSession.current) {
			currentSession.current = doc(
				db,
				"Users",
				user.uid,
				"sessions",
				new Date().getTime().toString()
			);
			await setDoc(currentSession.current, {
				createdAt: new Date(),
			});
			// If using free access, reduce the count at session start
			if (isFreeAccess) {
				const userRef = doc(db, "Users", user.uid);
				await updateDoc(userRef, {
					freeSessionsLeft: increment(-1),
				});
			}
		}
		if (user && message && messenger) {
			try {
				const fieldName =
					messenger === Messenger.AI
						? `message_${currentResponseNum.current[1]}B_AI`
						: `message_${currentResponseNum.current[0]}A_User`;

				// Encrypt the message
				const encryptedMessage = CryptoJS.AES.encrypt(
					message,
					encryptionKey
				).toString();

				await updateDoc(currentSession.current, {
					[fieldName]: encryptedMessage,
				});
			} catch (error) {
				console.error(`Error saving ${messenger} message:`, error);
				displayError(
					`Failed to save ${messenger} message. Please try again.`
				);
			}
		}
	};

	const handleInputModeToggle = () => {
		setUserMessage("");

		const newMode = inputMode === "voice" ? "text" : "voice";
		setInputMode(newMode);
		inputModeRef.current = newMode;

		if (newMode === "text") {
			stopListening();
		} else {
			if (modelReadyRef.current) {
				startListening();
			} else {
				displayError(
					"Model not ready yet. Please wait a moment before using voice input."
				);
				setInputMode("text");
				inputModeRef.current = "text";
			}
		}
	};

	const handleTextInputChange = (e) => {
		setTextInput(e.target.value);
	};

	const handleTextInputSubmit = (e) => {
		e.preventDefault();
		if (textInput.trim()) {
			// Stop any playing audio first
			if (isSpeaking) {
				stopSpeaking();
			}

			currentMessage.current = textInput;
			setUserMessage(textInput);
			currentResponseNum.current[0] += 1;
			saveEveryMessage(textInput, Messenger.USER);
			setTextInput("");
			setIsBuffering(true);
			fetchGeminiResponse();
		}
	};

	const handleVoiceOutputToggle = () => {
		setVoiceOutputEnabled(!voiceOutputEnabled);
		voiceEnabledRef.current = !voiceEnabledRef.current;
		if (isSpeaking) {
			stopSpeaking();
		}
	};

	const handlePause = () => {
		sessionPaused.current = true;
		if (isSpeaking) {
			stopSpeaking(false);
		}
		if (isListening) {
			stopListening();
		}
	};

	// Add this function to create session summary
	const createSessionSummary = async () => {
		if (currentSession.current && user) {
			try {
				const response = await fetch(
					"/.netlify/functions/create-session-summary",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							sessionId: currentSession.current.id,
							userId: user.uid,
							encryptionKey: encryptionKey,
						}),
					}
				);

				if (!response.ok) {
					throw new Error("Failed to create session summary");
				}
			} catch (error) {
				console.error("Error creating session summary:", error);
			}
		}
	};

	// Modify the cleanup useEffect
	useEffect(() => {
		return () => {
			// Existing cleanup code
			if (audioElement.current) {
				try {
					audioElement.current.pause();
					audioElement.current.currentTime = 0;
				} catch (error) {
					console.error(
						"Error stopping audio during cleanup:",
						error
					);
				}
			}
			if (recognition.current) {
				recognition.current.abort();
			}

			// Add session summary creation
			createSessionSummary();
		};
	}, []);

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 therapy-section">
			<Joyride
				steps={TherapySteps}
				run={runTour}
				continuous={true}
				showSkipButton={true}
				showProgress={false}
				styles={{
					options: {
						primaryColor: "#60a5fa",
						textColor: "#374151",
					},
				}}
				locale={{
					last: "Start talking!",
				}}
				callback={(data) => {
					const { status } = data;
					if (status === "finished" || status === "skipped") {
						setRunTour(false);
					}
				}}
			/>
			{isLoading ? (
				<div className="w-full max-w-2xl backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden flex flex-col bg-white p-8">
					<div className="flex flex-col items-center justify-center space-y-4">
						<Loader
							size={32}
							className="animate-spin text-blue-600"
						/>
						<p className="text-center text-gray-700 font-medium">
							Loading, please wait...
						</p>
					</div>
				</div>
			) : showTriager && !runTour ? (
				<Triager
					user={user}
					onComplete={async (recommendation) => {
						try {
							const userRef = doc(db, "Users", user.uid);
							await updateDoc(userRef, {
								triage: recommendation,
							});
							setShowTriager(false);
							setTriageMessage(recommendation);
							await prepareModel(
								user.uid,
								user.displayName,
								recommendation
							);
						} catch (error) {
							console.error("Error saving triage result:", error);
							displayError(
								"Failed to save therapy recommendation"
							);
						}
					}}
				/>
			) : !modelReady && !runTour ? (
				<div className="w-full max-w-2xl backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden flex flex-col bg-white p-8">
					<div className="flex flex-col items-center justify-center space-y-4">
						<Loader
							size={32}
							className="animate-spin text-blue-600"
						/>
						<p className="text-center text-gray-700 font-medium">
							Preparing to be tailored to you...
						</p>
					</div>
				</div>
			) : (
				<div className="w-full max-w-3xl h-[calc(var(--vh,1vh)*85)] backdrop-blur-lg rounded-3xl shadow-xl flex flex-col bg-white relative">
					{triageMessage && (
						<div className="absolute top-4 left-4 right-4 bg-blue-50 p-4 rounded-lg shadow-md z-10 border border-blue-200">
							<p className="text-sm text-blue-800">
								Based on your responses, the{" "}
								<span className="font-bold">
									{triageMessage}
								</span>{" "}
								therapy style seems most appropriate for you
							</p>
							<button
								className="mt-2 text-xs text-blue-600 hover:text-blue-900"
								onClick={() => setTriageMessage(false)}>
								Got it
							</button>
						</div>
					)}
					<div className="w-full text-center p-4 flex-shrink-0">
						<h1 className="text-3xl sm:text-4xl font-thin text-blue-900 tracking-wide m-3 sm:m-4">
							Hey
							<span className="font-normal text-blue-800">
								Akua
							</span>{" "}
						</h1>
						{showSuicidalBanner && (
							<div className="bg-yellow-500 text-yellow-900 p-3 rounded-lg mb-4 text-sm font-medium">
								For emergencies or serious issues, please seek
								professional help at: :<br />
								Suicide & Crisis Lifeline: Call or text 988
								(available 24/7). :<br />
								911:Emergency services for immediate,
								life-threatening situations.
								<br />
								Crisis Text Line: Text HOME to 741741 for
								confidential support via text
							</div>
						)}
						{showError && (
							<div className="bg-red-500 text-white p-3 rounded-md mb-4 text-sm font-small flex items-center">
								<AlertCircle size={18} className="mr-2" />
								<span>
									Very sorry! we encountered some errors
									processing the request: {error}
								</span>
							</div>
						)}
						{messageReported && (
							<div className="bg-green-800 text-white p-3 rounded-lg mb-4 text-sm font-medium">
								Message reported successfully, thank you for
								helping us improve!
							</div>
						)}
					</div>
					<div className="flex justify-center w-1/2 mx-auto mb-4 h-16 flex-shrink-0">
						<VoiceVisualization
							isActive={isListening || isSpeaking}
						/>
					</div>
					<div className="flex-1 flex flex-col items-center p-2 sm:p-4 overflow-y-auto">
						{isBuffering && (
							<div className="mt-4">
								<Loader className="animate-spin" size={24} />
							</div>
						)}
						<div className="w-full mt-4 sm:mt-6 space-y-3 sm:space-y-4">
							{userMessage && !isEditing && (
								<div className="bg-blue-600 text-white p-3 rounded-lg shadow-md max-w-[80%] self-end ml-auto">
									<p className="text-base sm:text-sm">
										{userMessage}
									</p>
									<button
										onClick={handleEditToggle}
										className="text-sm sm:text-xs text-gray-300 underline mt-2">
										Edit
									</button>
								</div>
							)}
							{isEditing && (
								<div className="bg-blue-600 text-white p-3 rounded-lg shadow-md max-w-[80%] self-end ml-auto">
									<textarea
										value={editedMessage}
										onChange={handleEditChange}
										className="w-full p-2 rounded-md text-black"
									/>
									<button
										onClick={handleEditSubmit}
										className="text-sm sm:text-xs text-gray-300 underline mt-2">
										Submit
									</button>
								</div>
							)}
							{aiResponse && (
								<div className="bg-gray-700 text-white p-3 rounded-lg shadow-md max-w-[80%]">
									<p className="text-base sm:text-sm">
										{aiResponse}
									</p>
									{!messageReported &&
										!messageEndorsed &&
										!messageBookmarked && (
											<div className="flex justify-end space-x-2">
												<button
													onClick={bookmarkMessage}
													className="text-sm sm:text-xs text-gray-300 mt-2 hover:text-gray-100 transition-colors">
													<Bookmark size={16} />
												</button>
												<button
													onClick={endorseMessage}
													className="text-sm sm:text-xs text-gray-300 mt-2">
													👍
												</button>
												<button
													onClick={reportMessage}
													className="text-sm sm:text-xs text-gray-300 mt-2">
													👎
												</button>
											</div>
										)}
									{messageBookmarked && (
										<div className="text-xs text-blue-400 mt-2 text-right">
											Message bookmarked!
										</div>
									)}
									{messageEndorsed && (
										<div className="text-xs text-green-400 mt-2 text-right">
											Message endorsed!
										</div>
									)}
									{messageReported && (
										<div className="text-xs text-red-400 mt-2 text-right">
											Message reported!
										</div>
									)}
								</div>
							)}
						</div>
					</div>
					<div className="p-2 sm:p-4 m-2 sm:m-4 flex-shrink-0">
						<div className="flex items-center justify-center space-x-3 sm:space-x-4">
							<Tooltip
								text={
									voiceOutputEnabled
										? "Disable voice output"
										: "Enable voice output"
								}>
								<button
									onClick={handleVoiceOutputToggle}
									className="p-4 rounded-full text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-300 shadow-lg shadow-green-500/50 voice-output-toggle">
									{voiceOutputEnabled ? (
										<Volume2 size={24} />
									) : (
										<VolumeX size={24} />
									)}
								</button>
							</Tooltip>

							<Tooltip
								text={`Switch to ${
									inputMode === "voice" ? "text" : "voice"
								} input`}>
								<button
									onClick={handleInputModeToggle}
									className="p-4 rounded-full text-white bg-purple-500 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all duration-300 shadow-lg shadow-purple-500/50 input-mode-toggle">
									{inputMode === "voice" ? "⌨️" : "🎤"}
								</button>
							</Tooltip>

							{inputMode === "voice" ? (
								<>
									<Tooltip
										text={
											isListening
												? "I'm done talking"
												: "Start listening"
										}>
										<button
											onClick={handleMicToggle}
											className={`p-4 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300 mic-toggle ${
												isListening
													? "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/50"
													: "bg-blue-500 hover:bg-blue-600 shadow-lg shadow-blue-500/50"
											}`}>
											{isListening ? (
												<MicOff size={24} />
											) : (
												<Mic size={24} />
											)}
										</button>
									</Tooltip>
								</>
							) : (
								<form
									onSubmit={handleTextInputSubmit}
									className="flex-1 flex">
									<input
										type="text"
										value={textInput}
										onChange={handleTextInputChange}
										placeholder="Type your message..."
										className="flex-1 p-2 rounded-l-lg focus:outline-none"
									/>
									<button
										type="submit"
										className="p-2 rounded-r-lg bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300">
										<Send size={24} />
									</button>
								</form>
							)}

							{isSpeaking && (
								<Tooltip text="Stop speaking">
									<button
										onClick={stopSpeaking}
										className="p-4 rounded-full text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-300 shadow-lg shadow-yellow-500/50">
										<VolumeX size={24} />
									</button>
								</Tooltip>
							)}

							{inputMode === "voice" &&
								(isListening || isSpeaking) && (
									<Tooltip text="Pause session">
										<button
											onClick={handlePause}
											className="p-4 rounded-full text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all duration-300 shadow-lg shadow-gray-500/50">
											<Pause size={24} />
										</button>
									</Tooltip>
								)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Therapist;
